/**
 * Question Manager Module
 * Manages question flow, progress tracking, and answer validation
 * Extracted from main.js as part of Phase 2 modularization
 */

import { EVENT_TYPES } from '../../core/event-types.js';
import { logEvent } from '../../core/event-logger.js';
import appState from '../../core/app-state.js';
import { i18nService } from '../../services/i18n-service.js';
import * as TranscriptModule from '../player/transcript.js';
import { AUTO_ADVANCE_ENABLED } from '../../config/feature-flags.js';
import { generateOptionLabel, shuffleQuestionOptions } from './question-utils.js';
import { switchMode } from '../../core/router.js';
import { markClipCompleted, updateProgressUI, isClipCompleted, setCurrentClip } from '../../services/clip-coordinator.js';
import type { Clip, Question, MultipleChoiceQuestion } from '../../types/clip.js';

// Re-export utilities for backwards compatibility
export { generateOptionLabel, shuffleQuestionOptions };

// Callback types
type RenderCurrentQuestionCallback = (clip: Clip, idx: number) => void;
type HandleNextClipCallback = () => void;
type RenderReportIssueButtonCallback = (clip: Clip, container: HTMLElement) => void;

// Card state interface for analytics
interface CardState {
  index: number;
  state: string;
  dimmed: boolean;
}

// Callback for rendering current question (registered by question-coordinator to avoid circular dependency)
let renderCurrentQuestionCallback: RenderCurrentQuestionCallback | null = null;

// Callbacks for UI functions from main.js (to avoid circular dependency)
let handleNextClipCallback: HandleNextClipCallback | null = null;
let renderReportIssueButtonCallback: RenderReportIssueButtonCallback | null = null;

/**
 * Register the renderCurrentQuestion callback
 * @param callback - The renderCurrentQuestion function
 */
export function registerRenderCurrentQuestion(callback: RenderCurrentQuestionCallback): void {
  renderCurrentQuestionCallback = callback;
}

/**
 * UI Callbacks interface
 */
interface UICallbacks {
  handleNextClip: HandleNextClipCallback;
  renderReportIssueButton: RenderReportIssueButtonCallback;
}

/**
 * Register UI callbacks from main.js
 * @param callbacks - Object with handleNextClip and renderReportIssueButton
 */
export function registerUICallbacks(callbacks: UICallbacks): void {
  handleNextClipCallback = callbacks.handleNextClip;
  renderReportIssueButtonCallback = callbacks.renderReportIssueButton;
}

/**
 * Question statistics interface
 */
interface QuestionStats {
  answered: number;
  total: number;
}

/**
 * Get current clip's question statistics
 * @returns Object with answered and total question counts
 */
export function getCurrentClipQuestionStats(): QuestionStats {
  const currentClip = appState.clips[appState.currentClipIdx];
  if (!currentClip) return { answered: 0, total: 0 };
  
  const totalQuestions = currentClip.questions?.length || 0;
  const answeredQuestions = Math.min(appState.currentQuestionIndex, totalQuestions);
  
  return { answered: answeredQuestions, total: totalQuestions };
}

/**
 * Update the question progress bar UI
 */
export function updateQuestionProgressBar(): void {
  const progressOuterEl = document.querySelector('.clip-progress-outer');
  const progressInnerEl = document.querySelector('.clip-progress-inner');
  const progressBarWrapper = document.getElementById('clipProgressBarWrapper');
  
  if (!progressInnerEl || !progressOuterEl || !progressBarWrapper || appState.clips.length === 0) return;
  
  const { answered, total } = getCurrentClipQuestionStats();
  
  // Only hide progress bar for clips with no questions
  if (total === 0) {
    // Hide progress bar
    progressBarWrapper.classList.add('clip-progress-hidden');
    
    // Announce to screen readers
    TranscriptModule.announceToScreenReader("No questions");
  } else {
    // Show progress bar
    progressBarWrapper.classList.remove('clip-progress-hidden');
    
    // Announce progress to screen readers (visually hidden)
    TranscriptModule.announceToScreenReader(`Answered ${answered} of ${total} questions`);
    
    // Update progress bar width based on questions answered
    const progressPercentage = (answered / total) * 100;
    if (progressInnerEl instanceof HTMLElement) {
      progressInnerEl.style.width = `${progressPercentage}%`;
    }
  }
}

/**
 * Schedule auto-advance to next question after answer
 * @param clip - The current clip
 */
export function scheduleAutoAdvance(clip: Clip): void {
  // Only proceed if auto-advance is enabled and this is not the final question
  if (!AUTO_ADVANCE_ENABLED || !clip || !clip.questions) {
    return;
  }

  const totalQuestions = clip.questions.length;
  const isLastQuestion = appState.currentQuestionIndex >= totalQuestions;

  // Never auto-advance on final question
  if (isLastQuestion) {
    return;
  }

  // Cancel any existing timeout
  cancelAutoAdvance();

  // Set up auto-advance for 1200ms
  appState.autoAdvanceActive = true;
  
  // Log auto_advance_trigger event
  logEvent(EVENT_TYPES['AUTO_ADVANCE_TRIGGER']!, {
    clipId: appState.activeClipId,
    questionIndex: appState.currentQuestionIndex,
    delayMs: 1200
  });

  // Announce to screen reader (match actual 1200ms delay)
  TranscriptModule.announceToScreenReader('Advancing to next question in 1.2 seconds…');

  appState.autoAdvanceTimeoutId = setTimeout(() => {
    // Check if auto-advance is still active (not cancelled)
    if (appState.autoAdvanceActive) {
      appState.autoAdvanceActive = false;
      appState.autoAdvanceTimeoutId = null;
      
      // Proceed to next question using registered callback
      if (renderCurrentQuestionCallback) {
        renderCurrentQuestionCallback(clip, appState.currentClipIdx);
      }
    }
  }, 1200) as unknown as number;
}

/**
 * Cancel any pending auto-advance
 */
export function cancelAutoAdvance(): void {
  if (appState.autoAdvanceTimeoutId) {
    clearTimeout(appState.autoAdvanceTimeoutId);
    appState.autoAdvanceTimeoutId = null;
  }
  
  if (appState.autoAdvanceActive) {
    appState.autoAdvanceActive = false;
    
    // Log auto_advance_cancel event
    logEvent(EVENT_TYPES['AUTO_ADVANCE_CANCEL']!, {
      clipId: appState.activeClipId,
      questionIndex: appState.currentQuestionIndex
    });
  }
}

/**
 * Show continue/finish button after answering
 * @param clip - The current clip
 * @param question - The answered question (unused but kept for interface consistency)
 * @param questionDiv - The question container element
 */
export function showExplanationAndContinue(clip: Clip, question: Question, questionDiv: HTMLElement): void {
  // Clear the global feedback region (for backwards compatibility)
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
  }
  
  // Increment currentQuestionIndex to track that we've answered this question
  appState.currentQuestionIndex++;
  
  // Check if there are more questions
  if (appState.currentQuestionIndex < clip.questions.length) {
    // Not the last question - add a continue button to proceed to the next question
    const continueBtn = document.createElement('button');
    continueBtn.id = 'nextQuestionInClip';
    continueBtn.textContent = i18nService.getString('continue') || 'Continue';
    continueBtn.className = 'inline-continue-btn';
    
    continueBtn.addEventListener('click', () => {
      // Cancel any pending auto-advance
      cancelAutoAdvance();
      // Render the next question using registered callback
      if (renderCurrentQuestionCallback) {
        renderCurrentQuestionCallback(clip, appState.currentClipIdx);
      }
    });
    
    questionDiv.appendChild(continueBtn);
    
    // Schedule auto-advance for non-final questions (if experiment is enabled)
    scheduleAutoAdvance(clip);
  } else {
    // This was the last question - mark clip as completed and show summary inline
    markClipCompleted(clip.id);
    updateProgressUI();
    
    // Create a container for the summary that will be appended below the question
    const summaryContainer = document.createElement('div');
    summaryContainer.className = 'inline-summary-container mt-4';
    questionDiv.appendChild(summaryContainer);
    
    // Render the summary chip into this container
    renderSummaryChip(clip, summaryContainer);
  }
  
  // Update the question progress bar
  updateQuestionProgressBar();
}

/**
 * Handle answer selection for multiple choice questions
 * @param _event - The click event (unused but kept for interface consistency)
 * @param clip - The current clip
 * @param question - The current question
 * @param selectedOptionIndex - The index of selected option (in shuffled array)
 * @param buttons - Array of answer button elements
 * @param questionDiv - The question container element
 * @param shuffledAnswerIndex - The correct answer index in shuffled array
 * @param originalOptionIndex - The original index before shuffling
 */
export function handleAnswerSelection(
  _event: Event, 
  clip: Clip, 
  question: Question, 
  selectedOptionIndex: number, 
  buttons: HTMLButtonElement[], 
  questionDiv: HTMLElement, 
  shuffledAnswerIndex: number, 
  originalOptionIndex: number
): void {
  // Check if buttons are already answered (disabled)
  const firstButton = buttons[0];
  if (!firstButton || firstButton.disabled) {
    return; // Exit early if already answered
  }
  
  // Cancel any pending auto-advance
  cancelAutoAdvance();
  
  // Cast to MultipleChoiceQuestion since this function only handles those
  const mcq = question as MultipleChoiceQuestion;
  
  // Use the shuffled answer index passed from renderCurrentQuestion
  // This ensures we compare against the correct position in the shuffled array
  const correctAnswerIndex = shuffledAnswerIndex !== undefined ? shuffledAnswerIndex : mcq.answerIndex;
        
  // Store whether this answer was correct for the summary
  const isCorrect = selectedOptionIndex === correctAnswerIndex;
  
  // Initialize attempt tracking if not present
  if (!appState.questionAttempts) {
    appState.questionAttempts = {};
  }
  const questionKey = `${clip.id}-${appState.currentQuestionIndex}`;
  const attempts = (appState.questionAttempts[questionKey] || 0) + 1;
  appState.questionAttempts[questionKey] = attempts;
  
  const MAX_ATTEMPTS = 2; // Allow 2 tries before revealing
  
  // Log answer_select event with original indices for analytics
  logEvent(EVENT_TYPES['ANSWER_SELECT']!, {
    clipId: clip.id,
    questionIndex: appState.currentQuestionIndex,
    questionText: mcq.prompt,
    selectedOptionIndex: originalOptionIndex !== undefined ? originalOptionIndex : selectedOptionIndex,
    correctOptionIndex: mcq.answerIndex,
    isCorrect: isCorrect,
    attempts: attempts,
    timeToAnswer: Date.now() - (appState.questionStartTime || Date.now())
  });
  
  if (isCorrect) {
    // Initialize the answers array for this clip if needed
    if (!appState.clipAnswers.has(clip.id)) {
      appState.clipAnswers.set(clip.id, []);
    }
    // Store the answer for the current question
    const answers = appState.clipAnswers.get(clip.id);
    if (answers) {
      answers[appState.currentQuestionIndex] = isCorrect;
    }
    
    // Correct answer: lock and proceed
    // Style buttons based on correct/incorrect answer and disable all
    const cardStates: CardState[] = []; // Track states for analytics
    buttons.forEach((b, i) => {
      // Lock the button visually and functionally
      b.classList.add('locked');

      // Get badge and text content
      const badge = b.querySelector('.answer-badge');
      const textSpan = b.querySelector('.answer-text');
      const buttonText = textSpan ? textSpan.textContent : '';

      if (i === selectedOptionIndex && i === correctAnswerIndex) {
        // Selected and correct - keep full opacity
        b.className = 'answer-card correct locked';
        cardStates.push({ index: i, state: 'selected-correct', dimmed: false });
        // Enhanced accessibility: Add attributes for ARIA
        b.setAttribute('aria-selected', 'true');
        b.setAttribute('aria-describedby', `answer-feedback-${clip.id}-${appState.currentQuestionIndex}`);

        // Preserve badge and add SR text
        if (badge && textSpan) {
          // Keep existing structure but add enhanced feedback
          const srFeedback = document.createElement('span');
          srFeedback.className = 'sr-only';
          srFeedback.id = `answer-feedback-${clip.id}-${appState.currentQuestionIndex}`;
          srFeedback.textContent = 'Correct answer. Well done!';
          b.appendChild(srFeedback);
        } else {
          // Fallback if structure was lost - create elements safely to avoid XSS
          b.textContent = buttonText;
          const srFeedback = document.createElement('span');
          srFeedback.className = 'sr-only';
          srFeedback.id = `answer-feedback-${clip.id}-${appState.currentQuestionIndex}`;
          srFeedback.textContent = 'Correct answer. Well done!';
          b.appendChild(srFeedback);
        }
      } else {
        // Dim other options
        b.className = 'answer-card dim-unselected locked';
        cardStates.push({ index: i, state: 'not-selected-incorrect', dimmed: true });
        // Add ARIA attributes for accessibility
        b.setAttribute('aria-selected', 'false');
      }
    });
    
    // Log answer_card_state_applied event for analytics
    logEvent(EVENT_TYPES['ANSWER_CARD_STATE_APPLIED']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      selectedOptionIndex: selectedOptionIndex,
      correctOptionIndex: correctAnswerIndex,
      cardStates: cardStates,
      timestamp: Date.now()
    });
    
    // Show continue button
    showExplanationAndContinue(clip, question, questionDiv);
    
  } else if (attempts < MAX_ATTEMPTS) {
    // Wrong answer, but retries remaining: flash red, DON'T reveal
    const btn = buttons[selectedOptionIndex];
    if (btn) {
      btn.classList.add('incorrect-flash');
      
      TranscriptModule.announceToScreenReader(i18nService.getString('tryAgainIncorrect') || 'Incorrect, try again');
      
      // Remove flash after animation
      setTimeout(() => {
        btn.classList.remove('incorrect-flash');
      }, 500);
    }
    
    // Log attempt
    logEvent(EVENT_TYPES['ANSWER_RETRY']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      attempts: attempts,
      selectedOptionIndex: selectedOptionIndex
    });
    
  } else {
    // Max attempts reached: reveal correct answer
    // Initialize the answers array for this clip if needed
    if (!appState.clipAnswers.has(clip.id)) {
      appState.clipAnswers.set(clip.id, []);
    }
    // Store the answer for the current question (marked as incorrect after max attempts)
    const answers = appState.clipAnswers.get(clip.id);
    if (answers) {
      answers[appState.currentQuestionIndex] = false;
    }
    
    // Style buttons based on correct/incorrect answer and disable all
    const cardStates: CardState[] = []; // Track states for analytics
    buttons.forEach((b, i) => {
      // Lock the button visually and functionally
      b.classList.add('locked');

      // Get badge and text content
      const badge = b.querySelector('.answer-badge');
      const textSpan = b.querySelector('.answer-text');
      const buttonText = textSpan ? textSpan.textContent : '';

      if (i === selectedOptionIndex) {
        // Selected but incorrect - keep full opacity
        b.className = 'answer-card incorrect locked';
        cardStates.push({ index: i, state: 'selected-incorrect', dimmed: false });
        // Enhanced accessibility: Add attributes for ARIA
        b.setAttribute('aria-selected', 'true');
        b.setAttribute('aria-describedby', `answer-feedback-${clip.id}-${appState.currentQuestionIndex}-incorrect`);

        if (badge && textSpan) {
          // Keep existing structure but add enhanced feedback
          const srFeedback = document.createElement('span');
          srFeedback.className = 'sr-only';
          srFeedback.id = `answer-feedback-${clip.id}-${appState.currentQuestionIndex}-incorrect`;
          srFeedback.textContent = 'Incorrect answer. The correct answer will be highlighted.';
          b.appendChild(srFeedback);
        } else {
          // Fallback if structure was lost - create elements safely to avoid XSS
          b.textContent = buttonText;
          const srFeedback = document.createElement('span');
          srFeedback.className = 'sr-only';
          srFeedback.id = `answer-feedback-${clip.id}-${appState.currentQuestionIndex}-incorrect`;
          srFeedback.textContent = 'Incorrect answer. The correct answer will be highlighted.';
          b.appendChild(srFeedback);
        }
      } else if (i === correctAnswerIndex) {
        // Not selected but is the correct answer - keep full opacity
        b.className = 'answer-card correct locked';
        cardStates.push({ index: i, state: 'correct-not-selected', dimmed: false });
        // Enhanced accessibility: Add attributes for ARIA
        b.setAttribute('aria-selected', 'false');
        b.setAttribute('aria-describedby', `answer-feedback-${clip.id}-${appState.currentQuestionIndex}-correct-not-selected`);

        if (badge && textSpan) {
          // Keep existing structure but add enhanced feedback
          const srFeedback = document.createElement('span');
          srFeedback.className = 'sr-only';
          srFeedback.id = `answer-feedback-${clip.id}-${appState.currentQuestionIndex}-correct-not-selected`;
          srFeedback.textContent = 'This was the correct answer';
          b.appendChild(srFeedback);
        } else {
          // Fallback if structure was lost
          b.textContent = buttonText;
          const srFeedback = document.createElement('span');
          srFeedback.className = 'sr-only';
          srFeedback.id = `answer-feedback-${clip.id}-${appState.currentQuestionIndex}-correct-not-selected`;
          srFeedback.textContent = 'This was the correct answer';
          b.appendChild(srFeedback);
        }
      } else {
        // Dim other options
        b.className = 'answer-card dim-unselected locked';
        cardStates.push({ index: i, state: 'not-selected', dimmed: true });
        b.setAttribute('aria-selected', 'false');
      }
    });
    
    // Log answer_card_state_applied event for analytics
    logEvent(EVENT_TYPES['ANSWER_CARD_STATE_APPLIED']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      selectedOptionIndex: selectedOptionIndex,
      correctOptionIndex: correctAnswerIndex,
      cardStates: cardStates,
      timestamp: Date.now()
    });
    
    // Show continue button
    showExplanationAndContinue(clip, question, questionDiv);
  }
}

/**
 * Render the summary chip after completing all questions
 * @param clip - The current clip
 * @param questionContainer - The question container element
 */
export function renderSummaryChip(clip: Clip, questionContainer: HTMLElement): void {
  // Access appFlags from window with proper typing
  const appFlags = (window as any).appFlags;
  
  // Clear the question container
  questionContainer.innerHTML = '';
  
  // Calculate performance statistics
  const hasQuestions = clip.questions && clip.questions.length > 0;
  let answersCorrect = 0;
  let totalQuestions = 0;
  let percentageRounded = 0;
  
  if (hasQuestions) {
    totalQuestions = clip.questions.length;
    
    // Calculate correct answers from stored tracking
    if (appState.clipAnswers.has(clip.id)) {
      const answers = appState.clipAnswers.get(clip.id);
      if (answers) {
        answersCorrect = answers.filter(answer => answer === true).length;
      }
    }
    
    percentageRounded = totalQuestions > 0 ? Math.round((answersCorrect / totalQuestions) * 100) : 0;
  }
  
  // Create summary chip container
  const summaryChip = document.createElement('div');
  summaryChip.className = 'summary-chip';
  summaryChip.setAttribute('role', 'group');
  summaryChip.setAttribute('aria-label', 'Clip completed');
  
  // Create message container
  const messageContainer = document.createElement('div');
  messageContainer.className = 'summary-chip-message-container';
  
  // Add stats line if there are questions
  if (hasQuestions) {
    const statsLine = document.createElement('p');
    statsLine.textContent = `${answersCorrect} / ${totalQuestions} correct (${percentageRounded}%)`;
    statsLine.className = 'summary-chip-stats';
    messageContainer.appendChild(statsLine);
    
    // Add encouragement message based on performance
    const encouragementMessage = document.createElement('p');
    encouragementMessage.className = 'summary-chip-encouragement';
    
    if (percentageRounded === 100) {
      encouragementMessage.textContent = 'Perfect! Excellent work!';
    } else if (percentageRounded >= 75) {
      encouragementMessage.textContent = 'Great job! Well done!';
    } else if (percentageRounded >= 50) {
      encouragementMessage.textContent = 'Good effort! Keep practicing!';
    } else {
      encouragementMessage.textContent = 'Keep learning and improving!';
    }
    
    messageContainer.appendChild(encouragementMessage);
  } else {
    // For zero-question clips, just show basic completion message
    const message = document.createElement('p');
    message.textContent = 'Clip completed.';
    message.className = 'summary-chip-message';
    messageContainer.appendChild(message);
  }
  
  summaryChip.appendChild(messageContainer);
  
  // Add feedback buttons if flag is enabled
  if (appFlags?.feedbackButtons && hasQuestions) {
    const feedbackContainer = document.createElement('div');
    feedbackContainer.className = 'feedback-buttons-container';
    feedbackContainer.style.cssText = 'display: flex; flex-direction: column; gap: 0.5rem; margin-top: 1rem; margin-bottom: 1rem; align-items: center;';
    
    const feedbackLabel = document.createElement('p');
    feedbackLabel.textContent = 'How was this clip?';
    feedbackLabel.style.cssText = 'font-size: 0.875rem; color: #6b7280; margin: 0; text-align: center;';
    feedbackContainer.appendChild(feedbackLabel);
    
    const feedbackButtonRow = document.createElement('div');
    feedbackButtonRow.style.cssText = 'display: flex; gap: 0.5rem; justify-content: center;';
    
    const tooEasyBtn = document.createElement('button');
    tooEasyBtn.textContent = 'Too Easy';
    tooEasyBtn.className = 'feedback-btn';
    tooEasyBtn.style.cssText = 'padding: 0.375rem 0.75rem; background-color: #f3f4f6; color: #374151; border: 1px solid #d1d5db; border-radius: 0.25rem; font-size: 0.875rem; cursor: pointer; transition: background-color 0.2s;';
    tooEasyBtn.setAttribute('aria-label', 'Provide feedback: too easy');
    
    tooEasyBtn.addEventListener('click', () => {
      logEvent(EVENT_TYPES['DIFFICULTY_FEEDBACK']!, {
        clipId: clip.id,
        difficulty: clip.difficulty || 'unknown',
        feedback: 'too_easy'
      });
      
      tooEasyBtn.disabled = true;
      tooHardBtn.disabled = true;
      tooEasyBtn.style.backgroundColor = '#d1fae5';
      tooEasyBtn.style.color = '#065f46';
      TranscriptModule.announceToScreenReader('Feedback recorded: too easy');
    });
    
    const tooHardBtn = document.createElement('button');
    tooHardBtn.textContent = 'Too Hard';
    tooHardBtn.className = 'feedback-btn';
    tooHardBtn.style.cssText = 'padding: 0.375rem 0.75rem; background-color: #f3f4f6; color: #374151; border: 1px solid #d1d5db; border-radius: 0.25rem; font-size: 0.875rem; cursor: pointer; transition: background-color 0.2s;';
    tooHardBtn.setAttribute('aria-label', 'Provide feedback: too hard');
    
    tooHardBtn.addEventListener('click', () => {
      logEvent(EVENT_TYPES['DIFFICULTY_FEEDBACK']!, {
        clipId: clip.id,
        difficulty: clip.difficulty || 'unknown',
        feedback: 'too_hard'
      });
      
      tooEasyBtn.disabled = true;
      tooHardBtn.disabled = true;
      tooHardBtn.style.backgroundColor = '#fee2e2';
      tooHardBtn.style.color = '#991b1b';
      TranscriptModule.announceToScreenReader('Feedback recorded: too hard');
    });
    
    feedbackButtonRow.appendChild(tooEasyBtn);
    feedbackButtonRow.appendChild(tooHardBtn);
    feedbackContainer.appendChild(feedbackButtonRow);
    
    summaryChip.appendChild(feedbackContainer);
  }
  
  // Create button container
  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'summary-chip-buttons';
  
  // Check if this is the last clip in the daily set
  const currentClipId = clip.id;
  const currentIndexInDailySet = appState.dailySet.indexOf(currentClipId);
  const isLastClipInDailySet = currentIndexInDailySet !== -1 && currentIndexInDailySet === appState.dailySet.length - 1;
  
  if (isLastClipInDailySet) {
    // Show completion message and navigation buttons for last clip in daily set
    const completionMessage = document.createElement('div');
    completionMessage.className = 'daily-goal-complete-message';
    completionMessage.style.cssText = 'text-align: center; padding: 1.25rem 1rem; margin: 0.75rem 0; background-color: #d1fae5; border-radius: 0.375rem; color: #065f46; font-weight: 600; font-size: 1rem; border: 1px solid #a7f3d0;';
    completionMessage.textContent = i18nService.getString('doneForToday') || "Well done! You've completed your daily goal.";
    buttonContainer.appendChild(completionMessage);
    
    // Add navigation buttons
    const navButtonsRow = document.createElement('div');
    navButtonsRow.style.cssText = 'display: flex; gap: 0.5rem; margin-top: 0.5rem;';
    
    const homeBtn = document.createElement('button');
    homeBtn.textContent = i18nService.getString('backToHome') || 'Back to Home';
    homeBtn.className = 'summary-chip-btn summary-chip-btn-secondary';
    homeBtn.style.cssText = 'flex: 1; background-color: #e5e7eb; color: #1f2937; padding: 0.625rem 1rem; font-weight: 500;';
    homeBtn.setAttribute('aria-label', 'Go back to home');
    
    homeBtn.addEventListener('click', () => {
      logEvent(EVENT_TYPES['SUMMARY_CHIP_DISMISS']!, {
        clipId: clip.id,
        action: 'go_home'
      });
      switchMode('home');
    });
    
    const libraryBtn = document.createElement('button');
    libraryBtn.textContent = i18nService.getString('viewLibrary') || 'View Library';
    libraryBtn.className = 'summary-chip-btn summary-chip-btn-primary';
    libraryBtn.style.cssText = 'flex: 1; padding: 0.625rem 1rem; font-weight: 500;';
    libraryBtn.setAttribute('aria-label', 'Go to library');
    
    libraryBtn.addEventListener('click', () => {
      logEvent(EVENT_TYPES['SUMMARY_CHIP_DISMISS']!, {
        clipId: clip.id,
        action: 'view_library'
      });
      switchMode('library');
    });
    
    navButtonsRow.appendChild(homeBtn);
    navButtonsRow.appendChild(libraryBtn);
    buttonContainer.appendChild(navButtonsRow);
  } else {
    // Show regular Next Clip button for non-last clips
    const nextBtn = document.createElement('button');
    nextBtn.textContent = i18nService.getString('nextClip') || 'Next Clip';
    nextBtn.className = 'summary-chip-btn summary-chip-btn-next';
    nextBtn.setAttribute('aria-label', 'Go to next clip');
    
    nextBtn.addEventListener('click', () => {
      // Log summary chip dismiss event
      logEvent(EVENT_TYPES['SUMMARY_CHIP_DISMISS']!, {
        clipId: clip.id,
        action: 'next_clip'
      });
      
      // Use existing next clip logic
      if (handleNextClipCallback) {
        handleNextClipCallback();
      }
    });
    
    buttonContainer.appendChild(nextBtn);
  }
  
  summaryChip.appendChild(buttonContainer);
  
  // Add chip to question container
  questionContainer.appendChild(summaryChip);
  
  // Focus the chip container for keyboard navigation
  summaryChip.setAttribute('tabindex', '-1');
  summaryChip.focus();
  
  // Announce completion to screen readers
  if (isLastClipInDailySet) {
    const message = hasQuestions 
      ? `Clip completed. You scored ${answersCorrect} out of ${totalQuestions} correct. Congratulations! You've completed your daily goal.`
      : "Clip completed. Congratulations! You've completed your daily goal.";
    TranscriptModule.announceToScreenReader(message);
  } else {
    if (hasQuestions) {
      TranscriptModule.announceToScreenReader(`Clip completed. You scored ${answersCorrect} out of ${totalQuestions} correct. You can go to the next clip.`);
    } else {
      TranscriptModule.announceToScreenReader('Clip completed. You can go to the next clip.');
    }
  }
  
  // Log summary chip view event
  logEvent(EVENT_TYPES['SUMMARY_CHIP_VIEW']!, {
    clipId: clip.id,
    answersCorrect: answersCorrect,
    totalQuestions: totalQuestions,
    percentageRounded: percentageRounded
  });
  
  // Clear the feedback region since we're using inline chip instead
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
    feedbackRegion.setAttribute('aria-hidden', 'true');
  }
  
  // Add Report Issue button if clip is completed
  if (isClipCompleted(clip.id)) {
    if (renderReportIssueButtonCallback) {
      renderReportIssueButtonCallback(clip, summaryChip);
    }
  }
}

// Export all functions as default object for convenient importing
export default {
  getCurrentClipQuestionStats,
  updateQuestionProgressBar,
  generateOptionLabel,
  shuffleQuestionOptions,
  scheduleAutoAdvance,
  cancelAutoAdvance,
  showExplanationAndContinue,
  handleAnswerSelection,
  renderSummaryChip
};
